import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
// @mui
import {
  Card,
  Stack,
  Button,
  Container,
  Typography,
  CircularProgress,
  Box,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  IconButton,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  FormControlLabel,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
// components

import Iconify from "../components/iconify";
import {
  getBlobsInContainer,
  createBlobInContainer,
  deleteBlobIfItExists,
} from "src/utils/azureBlob";
import { useBanners } from "../hooks/useBanners";
import { getEnabledLangs } from "../utils/lang";
// ----------------------------------------------------------------------

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`banners-tabpanel-${index}`}
      aria-labelledby={`banners-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `banners-tab-${index}`,
    "aria-controls": `banners-tabpanel-${index}`,
  };
}

const LANGS = getEnabledLangs();

const buildEmptyI18n = () => {
  const obj = {};
  LANGS.forEach(({ value }) => { obj[value] = ""; });
  return obj;
};

export default function BannersPage() {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState(null);
  
  // Special banners state
  const { banners, bannerTypes, loading: bannersLoading, fetchBanners, createBanner, updateBanner, deleteBanner } = useBanners();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [langTab, setLangTab] = useState(0);
  const [listLang, setListLang] = useState(LANGS[0]?.value || "en");
  const [formData, setFormData] = useState({
    code: "",
    type: "",
    status: true,
    image_url_i18n: buildEmptyI18n(),
    redirect_i18n: buildEmptyI18n(),
  });

  useEffect(() => {
    const fetchImages = async () => {
      await getContainerImages();
    };

    fetchImages().catch(console.error);
    fetchBanners().catch(console.error);
  }, [fetchBanners]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getContainerImages = async () => {
    const data = await getBlobsInContainer("banners/");
    setImages(data);
  };

  const deleteImageHandler = async (value) => {
    await deleteBlobIfItExists(value);
    window.location.reload(false);
  };

  const uploadFiles = (event) => {
    if (event.target.files.length > 0) {
      setLoading(true);
      const files = event.target.files;
      let uploadPromises = [];
      for (let i = 0; i < files.length; i++) {
        uploadPromises.push(createBlobInContainer(files[i], "banners/"));
      }
      if (uploadPromises.length > 0) {
        const uploadFiles = async () => {
          await Promise.all(uploadPromises);
          await getContainerImages();
          setLoading(false);
        };

        uploadFiles().catch(console.error);
      }
    }
  };

  // Special banner handlers
  const handleOpenDialog = (banner = null) => {
    if (banner) {
      setEditingBanner(banner);
      setFormData({
        code: banner.code || "",
        type: (() => {
          if (banner.type == null) return "";
          const raw = String(banner.type);
          const byValue = bannerTypes.find((t) => String(t.value) === raw);
          if (byValue) return String(byValue.value);
          const byLabel = bannerTypes.find((t) => t.label === raw);
          if (byLabel) return String(byLabel.value);
          return raw;
        })(),
        status: banner.status ?? true,
        image_url_i18n: { ...buildEmptyI18n(), ...banner.image_url_i18n },
        redirect_i18n: { ...buildEmptyI18n(), ...banner.redirect_i18n },
      });
    } else {
      setEditingBanner(null);
      setFormData({ code: "", type: "", status: true, image_url_i18n: buildEmptyI18n(), redirect_i18n: buildEmptyI18n() });
    }
    setLangTab(0);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingBanner(null);
    setFormData({ code: "", type: "", status: true, image_url_i18n: buildEmptyI18n(), redirect_i18n: buildEmptyI18n() });
    setLangTab(0);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleI18nChange = (field, langCode, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: { ...prev[field], [langCode]: value },
    }));
  };

  const handleSaveBanner = async () => {
    try {
      const payload = {
        code: formData.code,
        type: formData.type,
        status: formData.status,
        image_url_i18n: formData.image_url_i18n,
        redirect_i18n: formData.redirect_i18n,
      };
      if (editingBanner) {
        await updateBanner(editingBanner.id, payload);
      } else {
        await createBanner(payload);
      }
      handleCloseDialog();
    } catch (error) {
      console.error("Error saving banner:", error);
    }
  };

  const handleDeleteBanner = async (id) => {
    if (window.confirm("Are you sure you want to delete this banner?")) {
      try {
        await deleteBanner(id);
      } catch (error) {
        console.error("Error deleting banner:", error);
      }
    }
  };

  const activeLangCode = LANGS[langTab]?.value || "en";

  const uploadBannerImage = async (event) => {
    if (event.target.files.length > 0) {
      setUploadingImage(true);
      const file = event.target.files[0];

      try {
        // Unique name per upload so the image URL changes and clients don't serve a cached copy
        const blobName = `${Date.now()}-${file.name}`;
        await createBlobInContainer(file, "banners/", blobName);
        
        const containerName = process.env.REACT_APP_STORAGE_CONTAINER;
        const storageAccountName = process.env.REACT_APP_STORAGERESOURCENAME;
        const imageUrl = `https://${storageAccountName}.blob.core.windows.net/${containerName}/banners/${blobName}`;
        
        setFormData((prev) => ({
          ...prev,
          image_url_i18n: { ...prev.image_url_i18n, [activeLangCode]: imageUrl },
        }));
      } catch (error) {
        console.error("Error uploading image:", error);
      } finally {
        setUploadingImage(false);
      }
    }
  };

  const handleLangTabChange = (event, newValue) => {
    setLangTab(newValue);
  };

  const defaultLang = LANGS[0]?.value || "en";
  const isSaveDisabled = !formData.type;

  return (
    <>
      <Helmet>
        <title> Banners </title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Banners
          </Typography>
        </Stack>

        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="banner tabs">
            <Tab label="Images" {...a11yProps(0)} />
            <Tab label="Special" {...a11yProps(1)} />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Stack direction="row" alignItems="center" justifyContent="flex-end" mb={2}>
            <Button
              variant="contained"
              component="label"
              startIcon={<Iconify icon="eva:plus-fill" />}
            >
              Upload Files
              <input
                hidden
                accept="image/*"
                multiple
                type="file"
                onChange={uploadFiles}
              />
            </Button>
          </Stack>

          <Card sx={{ p: 2 }}>
            {images === null || loading ? (
              <Box
                sx={{ display: "flex", justifyContent: "center" }}
                style={{ padding: 20 }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <ImageList cols={2} rowHeight={264}>
                {images.map((item, index) => (
                  <ImageListItem key={index}>
                    <img
                      src={`${item.blobUrl}?date=${new Date().getTime()}`}
                      srcSet={`${item.blobUrl}?date=${new Date().getTime()}`}
                      alt="library"
                      loading="lazy"
                    />
                    <ImageListItemBar
                      title={item.blobName}
                      subtitle={""}
                      actionIcon={
                        <IconButton
                          sx={{ color: "rgba(255, 255, 255, 0.54)" }}
                          aria-label={`info about ${item.title}`}
                          value={item.blobName}
                          onClick={() => {
                            deleteImageHandler(item.blobName);
                          }}
                        >
                          <Iconify icon="eva:trash-2-outline" />
                        </IconButton>
                      }
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            )}
          </Card>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Tabs
              value={listLang}
              onChange={(e, val) => setListLang(val)}
              aria-label="listing language filter"
              sx={{ minHeight: 36 }}
            >
              {LANGS.map((lang) => (
                <Tab
                  key={lang.value}
                  value={lang.value}
                  label={lang.label}
                  sx={{ minHeight: 36, py: 0.5 }}
                />
              ))}
            </Tabs>
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" />}
              onClick={() => handleOpenDialog()}
            >
              Add Special Banner
            </Button>
          </Stack>

          <Card>
            {bannersLoading ? (
              <Box
                sx={{ display: "flex", justifyContent: "center" }}
                style={{ padding: 20 }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Code</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Image</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Redirect URL</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {banners.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          No special banners found
                        </TableCell>
                      </TableRow>
                    ) : (
                      banners.map((banner) => {
                        const imgUrl = banner.image_url_i18n?.[listLang] || "";
                        const redirect = banner.redirect_i18n?.[listLang] || "";
                        return (
                          <TableRow key={banner.id}>
                            <TableCell>{banner.id}</TableCell>
                            <TableCell>{banner.code}</TableCell>
                            <TableCell>
                              {bannerTypes.find((t) => String(t.value) === String(banner.type) || t.label === banner.type)?.label || banner.type || "—"}
                            </TableCell>
                            <TableCell>
                              {imgUrl ? (
                                <Box
                                  component="img"
                                  src={imgUrl}
                                  alt={banner.code}
                                  sx={{
                                    width: 80,
                                    height: 48,
                                    objectFit: "cover",
                                    borderRadius: 1,
                                    border: "1px solid",
                                    borderColor: "divider",
                                  }}
                                />
                              ) : (
                                <Typography variant="caption" color="text.secondary">—</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={banner.status !== false ? "Visible" : "Hidden"}
                                color={banner.status !== false ? "success" : "default"}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {redirect ? (
                                <a
                                  href={redirect}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: "#1976d2", textDecoration: "none" }}
                                >
                                  {redirect.length > 50
                                    ? `${redirect.substring(0, 50)}...`
                                    : redirect}
                                </a>
                              ) : (
                                <Typography variant="caption" color="text.secondary">—</Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                color="primary"
                                onClick={() => handleOpenDialog(banner)}
                              >
                                <Iconify icon="eva:edit-fill" />
                              </IconButton>
                              <IconButton
                                color="error"
                                onClick={() => handleDeleteBanner(banner.id)}
                              >
                                <Iconify icon="eva:trash-2-outline" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        </TabPanel>

        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingBanner ? "Edit Special Banner" : "Add Special Banner"}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 2 }}>
              <TextField
                label="Code"
                value={formData.code}
                onChange={(e) => handleFormChange("code", e.target.value)}
                fullWidth
                helperText="Unique identifier for the banner"
              />

              <FormControl fullWidth required>
                <InputLabel id="banner-type-label">Type</InputLabel>
                <Select
                  labelId="banner-type-label"
                  value={formData.type}
                  label="Type"
                  onChange={(e) => handleFormChange("type", e.target.value)}
                >
                  {bannerTypes.map((bt) => (
                    <MenuItem key={bt.value} value={String(bt.value)}>
                      {bt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.status}
                    onChange={(e) => handleFormChange("status", e.target.checked)}
                  />
                }
                label="Visible"
              />

              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                  value={langTab}
                  onChange={handleLangTabChange}
                  aria-label="banner language tabs"
                >
                  {LANGS.map((lang, idx) => (
                    <Tab key={lang.value} label={lang.label} {...a11yProps(`lang-${idx}`)} />
                  ))}
                </Tabs>
              </Box>

              {LANGS.map((lang, idx) => (
                <TabPanel value={langTab} index={idx} key={lang.value}>
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Banner Image ({lang.label}) {idx === 0 && "*"}
                      </Typography>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Button
                          variant="outlined"
                          component="label"
                          startIcon={<Iconify icon="eva:upload-outline" />}
                          disabled={uploadingImage}
                        >
                          {uploadingImage ? "Uploading..." : "Upload Image"}
                          <input
                            hidden
                            accept="image/*"
                            type="file"
                            onChange={uploadBannerImage}
                          />
                        </Button>
                        {formData.image_url_i18n[lang.value] && (
                          <Box
                            component="img"
                            src={formData.image_url_i18n[lang.value]}
                            alt={`Banner preview (${lang.label})`}
                            sx={{
                              width: 100,
                              height: 60,
                              objectFit: "cover",
                              borderRadius: 1,
                              border: "1px solid",
                              borderColor: "divider",
                            }}
                          />
                        )}
                      </Stack>
                      {formData.image_url_i18n[lang.value] && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                          {formData.image_url_i18n[lang.value]}
                        </Typography>
                      )}
                    </Box>

                    <TextField
                      label={`Redirect (${lang.label})`}
                      value={formData.redirect_i18n[lang.value] || ""}
                      onChange={(e) => handleI18nChange("redirect_i18n", lang.value, e.target.value)}
                      fullWidth
                      helperText="URL to redirect when banner is clicked"
                    />
                  </Stack>
                </TabPanel>
              ))}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              onClick={handleSaveBanner}
              variant="contained"
              disabled={isSaveDisabled}
            >
              {editingBanner ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
}
